import { Menu, ExternalLink } from "lucide-react";
import * as LucideIcons from "lucide-react";
import logo from "@/assets/logo.png";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon?: string;
  requires_admin: boolean;
  is_external: boolean;
  display_order: number;
}

const Header = () => {
  const { user, signOut } = useAuth();
  const [profile, setProfile] = useState<{ avatar_url?: string; angler_name?: string } | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [navigationItems, setNavigationItems] = useState<NavigationItem[]>([]);

  useEffect(() => {
    if (user) {
      fetchProfile();
      checkAdminStatus();
      fetchNavigationItems();
    }
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('profiles')
        .select('avatar_url, angler_name')
        .eq('id', user.id)
        .single();
      
      if (data) setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const checkAdminStatus = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .rpc('has_role', { _user_id: user.id, _role: 'admin' });
      
      if (data) setIsAdmin(true);
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const fetchNavigationItems = async () => {
    try {
      const { data, error } = await supabase
        .from('navigation_items')
        .select('*')
        .eq('active', true)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      setNavigationItems(data || []);
    } catch (error) {
      console.error('Error fetching navigation items:', error);
    }
  };

  const getIcon = (iconName?: string) => {
    if (!iconName) return null;
    const Icon = (LucideIcons as any)[iconName];
    return Icon ? <Icon className="mr-2 h-4 w-4" aria-hidden="true" /> : null;
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-3" aria-label="Go to home page">
          <img src={logo} alt="Upper Room Fishing Club logo" className="h-10 w-auto" />
          <span className="text-xl font-bold text-gradient">Upper Room Fishing Club</span>
        </Link>

        {user && (
          <nav aria-label="Main navigation" className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2" aria-label="User profile">
              <Avatar className="h-8 w-8">
                <AvatarImage src={profile?.avatar_url} alt={`${profile?.angler_name || 'User'}'s profile picture`} />
                <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                  {profile?.angler_name?.charAt(0) || user.email?.charAt(0) || 'A'}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{profile?.angler_name || user.email}</span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open navigation menu">
                  <Menu className="h-5 w-5" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-background">
                {navigationItems
                  .filter(item => !item.requires_admin || isAdmin)
                  .map((item, index, array) => {
                    const isLastBeforeSeparator = item.path === '/profile' || item.path === '/admin';
                    const showSeparatorAfter = isLastBeforeSeparator && index < array.length - 1;
                    
                    return (
                      <div key={item.id}>
                        <DropdownMenuItem asChild>
                          {item.is_external ? (
                            <a 
                              href={item.path} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="cursor-pointer flex items-center"
                            >
                              {getIcon(item.icon)}
                              {item.label}
                              <ExternalLink className="ml-auto h-3 w-3" />
                            </a>
                          ) : (
                            <Link to={item.path} className="cursor-pointer">
                              {getIcon(item.icon)}
                              {item.label}
                            </Link>
                          )}
                        </DropdownMenuItem>
                        {showSeparatorAfter && <DropdownMenuSeparator />}
                      </div>
                    );
                  })}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="cursor-pointer text-destructive">
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;